/**
 * TileGrid — collision world over the LevelFormat dense grid.
 *
 * AABB-vs-grid resolution, X and Y resolved SEPARATELY (standard stepped AABB).
 * At 60 Hz, max per-step displacement (terminalVelocity ≈ 1250 px/s → ~21 px;
 * runMax ≈ 380 px/s → ~6 px) stays well under a 64 px tile, so no tunnelling —
 * single-step resolution is sufficient and kept simple per the brief.
 */
import { type LevelFormat, TileType } from './LevelFormat';

export interface AABB {
  x: number; // top-left world px
  y: number;
  w: number;
  h: number;
}

const EPS = 0.001;

export class TileGrid {
  readonly cols: number;
  readonly rows: number;
  readonly ts: number;
  readonly widthPx: number;
  readonly heightPx: number;
  private grid: TileType[][];

  constructor(level: LevelFormat) {
    this.grid = level.tiles;
    this.ts = level.dimensions.tileSize;
    this.cols = level.dimensions.widthTiles;
    this.rows = level.dimensions.heightTiles;
    this.widthPx = this.cols * this.ts;
    this.heightPx = this.rows * this.ts;
  }

  tileAt(col: number, row: number): TileType {
    if (col < 0 || col >= this.cols || row < 0 || row >= this.rows) return TileType.EMPTY;
    return this.grid[row][col];
  }
  isSolid(col: number, row: number): boolean { return this.tileAt(col, row) === TileType.SOLID; }
  isSemisolid(col: number, row: number): boolean { return this.tileAt(col, row) === TileType.SEMISOLID; }
  isGoal(col: number, row: number): boolean { return this.tileAt(col, row) === TileType.GOAL; }

  /** Move body by dx on X and resolve against SOLID tiles. Returns true if blocked. */
  collideX(body: AABB, dx: number): boolean {
    body.x += dx;
    if (dx === 0) return false;
    const rowTop = Math.floor((body.y + EPS) / this.ts);
    const rowBot = Math.floor((body.y + body.h - EPS) / this.ts);
    if (dx > 0) {
      const col = Math.floor((body.x + body.w - EPS) / this.ts);
      for (let row = rowTop; row <= rowBot; row++) {
        if (this.isSolid(col, row)) { body.x = col * this.ts - body.w; return true; }
      }
    } else {
      const col = Math.floor((body.x + EPS) / this.ts);
      for (let row = rowTop; row <= rowBot; row++) {
        if (this.isSolid(col, row)) { body.x = (col + 1) * this.ts; return true; }
      }
    }
    return false;
  }

  /**
   * Move body by dy on Y and resolve. SEMISOLID collides only when moving down
   * AND the body's feet were above the tile top last position (one-way platform).
   * Returns landing / ceiling flags.
   */
  collideY(body: AABB, dy: number, ignoreSemisolid: boolean): { grounded: boolean; ceiling: boolean } {
    const prevBottom = body.y + body.h;
    body.y += dy;
    const colL = Math.floor((body.x + EPS) / this.ts);
    const colR = Math.floor((body.x + body.w - EPS) / this.ts);
    let grounded = false;
    let ceiling = false;
    if (dy > 0) {
      const row = Math.floor((body.y + body.h - EPS) / this.ts);
      for (let col = colL; col <= colR; col++) {
        let hit = this.isSolid(col, row);
        if (!hit && !ignoreSemisolid && this.isSemisolid(col, row)) {
          if (prevBottom <= row * this.ts + 1) hit = true; // feet were above platform top
        }
        if (hit) { body.y = row * this.ts - body.h; grounded = true; break; }
      }
    } else if (dy < 0) {
      const row = Math.floor((body.y + EPS) / this.ts);
      for (let col = colL; col <= colR; col++) {
        if (this.isSolid(col, row)) { body.y = (row + 1) * this.ts; ceiling = true; break; } // semisolid never blocks from below
      }
    }
    return { grounded, ceiling };
  }

  /** True if the AABB overlaps any tile of the given type. */
  overlapsType(body: AABB, type: TileType): boolean {
    const colL = Math.floor((body.x + EPS) / this.ts);
    const colR = Math.floor((body.x + body.w - EPS) / this.ts);
    const rowT = Math.floor((body.y + EPS) / this.ts);
    const rowB = Math.floor((body.y + body.h - EPS) / this.ts);
    for (let r = rowT; r <= rowB; r++) {
      for (let c = colL; c <= colR; c++) {
        if (this.tileAt(c, r) === type) return true;
      }
    }
    return false;
  }

  /** Is there solid ground at world px (x) just below feet level y? (enemy ledge test) */
  solidBelowPoint(x: number, y: number): boolean {
    return this.isSolid(Math.floor(x / this.ts), Math.floor(y / this.ts));
  }

  /**
   * Is the AABB flush against (or within ~1.5px of) a SOLID tile on the given side?
   * `dir` = +1 right, -1 left. Probes a thin column just OUTSIDE that vertical edge
   * across the body's full height. Used by the PRECISION wall-slide / wall-jump verbs
   * (passive wall adjacency — no input). Non-mutating, integer-only (lint-clean).
   * SEMISOLID never counts — only full SOLID tiles are walls.
   */
  solidBeside(body: AABB, dir: 1 | -1): boolean {
    const probe = 1.5; // px just outside the edge
    const x = dir > 0 ? body.x + body.w + probe : body.x - probe;
    const col = Math.floor(x / this.ts);
    const rowTop = Math.floor((body.y + EPS) / this.ts);
    const rowBot = Math.floor((body.y + body.h - EPS) / this.ts);
    for (let row = rowTop; row <= rowBot; row++) {
      if (this.isSolid(col, row)) return true;
    }
    return false;
  }
}
